// Client Portal — Distribution engagement analytics (Buffer-parity item 3).
// Reads the broker's single aggregate endpoint over social_post_metrics
// snapshots: per-day engagement deltas (stacked by platform), current
// per-platform totals, and top posts.
// Platform colors are a fixed-order categorical palette validated for
// colorblind separation and contrast on the white card surface
// (dataviz six-checks validator) — change order/hues only if re-validated.
// ⚠️ All user-facing copy in this file is draft — flagged for human review
// (Hard Rule 8) before client exposure.
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, BarChart3 } from "lucide-react";
import { distributionFetch } from "../lib/api";
import { PLATFORM_LABELS } from "./DistributionCalendar";

const PLATFORM_COLORS = {
  facebook: "#1d4ed8",
  youtube: "#dc2626",
  tiktok: "#0d9488",
  instagram: "#c026d3",
  x: "#7c3aed",
  linkedin: "#0284c7",
};
// Stacking order — validated adjacency, never cycled or re-sorted.
const CHART_ORDER = [
  "facebook",
  "youtube",
  "tiktok",
  "instagram",
  "x",
  "linkedin",
];

const RANGES = [7, 30, 90];
const METRICS = [
  ["engagement", "Engagement"],
  ["impressions", "Impressions"],
];

const fmt = (n) => {
  if (n == null) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const dayKeysBack = (days) => {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
    out.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );
  }
  return out;
};

function StatTile({ label, value }) {
  return (
    <div className="border rounded-xl p-4 bg-white">
      <div className="text-2xl font-semibold">{fmt(value)}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export function DistributionAnalytics({ facility }) {
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState("engagement");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await distributionFetch(
        `/analytics/${facility.facility_id}?days=${days}`,
      );
      setData(res);
    } catch {
      setError("Could not load analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [facility.facility_id, days]);

  useEffect(() => {
    load();
  }, [load]);

  const platforms = useMemo(
    () =>
      CHART_ORDER.filter((p) =>
        (data?.platforms || []).some((t) => t.platform === p),
      ),
    [data],
  );

  // day -> platform -> metric value, zero-filled over the whole window
  const { dayKeys, stacked, maxTotal } = useMemo(() => {
    const keys = dayKeysBack(days);
    const byDay = {};
    for (const row of data?.daily || []) {
      (byDay[row.day] = byDay[row.day] || {})[row.platform] = row[metric] || 0;
    }
    let max = 0;
    const cols = keys.map((day) => {
      const segments = platforms
        .map((p) => ({ platform: p, value: byDay[day]?.[p] || 0 }))
        .filter((s) => s.value > 0);
      const total = segments.reduce((a, s) => a + s.value, 0);
      max = Math.max(max, total);
      return { day, segments, total };
    });
    return { dayKeys: keys, stacked: cols, maxTotal: max };
  }, [data, days, metric, platforms]);

  const totals = useMemo(() => {
    const sum = (f) =>
      (data?.platforms || []).reduce((a, t) => a + (t[f] || 0), 0);
    return {
      posts: sum("posts"),
      engagement: sum("engagement"),
      impressions: sum("impressions"),
    };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="p-4 text-gray-500 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
        {error}
      </div>
    );
  }

  const empty = !totals.posts && stacked.every((d) => d.total === 0);

  return (
    <div className="space-y-6">
      {/* Filter row — range + metric, one row above the charts */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border overflow-hidden text-sm">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              className={`px-3 py-1 ${days === r ? "bg-sky-600 text-white" : "hover:bg-gray-50"}`}
            >
              {r}d
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border overflow-hidden text-sm">
          {METRICS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setMetric(key)}
              className={`px-3 py-1 ${metric === key ? "bg-sky-600 text-white" : "hover:bg-gray-50"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {loading && (
          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {empty ? (
        <div className="border rounded-xl p-8 text-center bg-gray-50 text-gray-500">
          <BarChart3 className="w-8 h-8 mx-auto mb-3 text-gray-400" />
          <p className="text-sm">
            No engagement data yet. Metrics appear here after posts publish and
            the first collection sweep runs.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 grid-cols-3 max-w-xl">
            <StatTile
              label={`Posts (last ${days} days)`}
              value={totals.posts}
            />
            <StatTile label="Engagement" value={totals.engagement} />
            <StatTile label="Impressions" value={totals.impressions} />
          </div>

          {/* Daily stacked bars by platform */}
          <div className="border rounded-xl p-4 bg-white">
            <div className="text-sm font-medium text-gray-700 mb-2">
              {metric === "engagement"
                ? "Engagement gained per day"
                : "Impressions gained per day"}
            </div>
            <div className="text-right text-xs text-gray-400 mb-1">
              {fmt(maxTotal)}
            </div>
            <div className="flex items-end gap-px" style={{ height: "120px" }}>
              {stacked.map((d) => {
                const barPct = maxTotal > 0 ? (d.total / maxTotal) * 100 : 0;
                return (
                  <div
                    key={d.day}
                    className="flex-1 flex flex-col justify-end group relative"
                    style={{ height: "100%" }}
                  >
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                      <div className="font-medium">
                        {d.day.slice(5)} — {fmt(d.total)}
                      </div>
                      {d.segments.map((s) => (
                        <div
                          key={s.platform}
                          className="flex items-center gap-1"
                        >
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-sm"
                            style={{
                              backgroundColor: PLATFORM_COLORS[s.platform],
                            }}
                          />
                          {PLATFORM_LABELS[s.platform]?.label || s.platform}:{" "}
                          {fmt(s.value)}
                        </div>
                      ))}
                    </div>
                    <div
                      className="w-full flex flex-col justify-end overflow-hidden rounded-t"
                      style={{
                        height: `${Math.max(barPct, d.total > 0 ? 2 : 0)}%`,
                      }}
                    >
                      {[...d.segments].reverse().map((s, i) => (
                        <div
                          key={s.platform}
                          style={{
                            // 2px surface gap between stacked segments,
                            // subtracted from the segment so the stack still
                            // sums to the bar height
                            height: `calc(${(s.value / d.total) * 100}% - ${
                              i < d.segments.length - 1 ? 2 : 0
                            }px)`,
                            backgroundColor: PLATFORM_COLORS[s.platform],
                            minHeight: "2px",
                            marginBottom: i < d.segments.length - 1 ? "2px" : 0,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-px mt-1">
              {dayKeys.map((day, i) => (
                <div key={day} className="flex-1 text-center">
                  {i % Math.ceil(dayKeys.length / 8) === 0 && (
                    <span className="text-[9px] text-gray-400">
                      {day.slice(5)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {platforms.map((p) => (
                <div key={p} className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: PLATFORM_COLORS[p] }}
                  />
                  <span className="text-xs text-gray-600">
                    {PLATFORM_LABELS[p]?.label || p}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Per-platform totals — the accessible table view of the chart */}
          <div className="border rounded-xl bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="px-4 py-2">Platform</th>
                  <th className="px-4 py-2 text-right">Posts</th>
                  <th className="px-4 py-2 text-right">Impressions</th>
                  <th className="px-4 py-2 text-right">Likes</th>
                  <th className="px-4 py-2 text-right">Comments</th>
                  <th className="px-4 py-2 text-right">Shares</th>
                  <th className="px-4 py-2 text-right">Clicks</th>
                  <th className="px-4 py-2 text-right">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {(data?.platforms || []).map((t) => (
                  <tr key={t.platform} className="border-b last:border-0">
                    <td className="px-4 py-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-sm mr-2"
                        style={{ backgroundColor: PLATFORM_COLORS[t.platform] }}
                      />
                      {PLATFORM_LABELS[t.platform]?.label || t.platform}
                    </td>
                    <td className="px-4 py-2 text-right">{t.posts}</td>
                    <td className="px-4 py-2 text-right">
                      {fmt(t.impressions)}
                    </td>
                    <td className="px-4 py-2 text-right">{fmt(t.likes)}</td>
                    <td className="px-4 py-2 text-right">{fmt(t.comments)}</td>
                    <td className="px-4 py-2 text-right">{fmt(t.shares)}</td>
                    <td className="px-4 py-2 text-right">{fmt(t.clicks)}</td>
                    <td className="px-4 py-2 text-right font-medium">
                      {fmt(t.engagement)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top posts */}
          {(data?.top_posts || []).length > 0 && (
            <div className="border rounded-xl bg-white overflow-x-auto">
              <div className="text-sm font-medium text-gray-700 px-4 pt-3">
                Top posts
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="px-4 py-2">Post</th>
                    <th className="px-4 py-2">Platform</th>
                    <th className="px-4 py-2">Published</th>
                    <th className="px-4 py-2 text-right">Impressions</th>
                    <th className="px-4 py-2 text-right">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_posts.map((p) => (
                    <tr key={p.post_id} className="border-b last:border-0">
                      <td className="px-4 py-2 max-w-xs truncate">
                        {p.title || p.body_preview || p.content_id}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-sm mr-2"
                          style={{
                            backgroundColor: PLATFORM_COLORS[p.platform],
                          }}
                        />
                        {PLATFORM_LABELS[p.platform]?.label || p.platform}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                        {p.published_at
                          ? new Date(p.published_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {fmt(p.impressions)}
                      </td>
                      <td
                        className="px-4 py-2 text-right font-medium"
                        title={`${p.likes} likes · ${p.comments} comments · ${p.shares} shares`}
                      >
                        {fmt(p.engagement)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
